import React from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Flex, Box, Tabs, TabList, TabPanels, Tab, TabPanel, Text, HStack, StackDivider } from '@chakra-ui/react';
import NoDataCard from 'components/Cards/NoDataCard';
import LoadingDataCard from 'components/Cards/LoadingDataCard';
import ErrorCard from 'components/Cards/ErrorCard';
import { useGetTransactionByIdQuery } from 'reducers/transaction.service';
import TransactionDetailsCard from './TransactionDetailsCard';
import PropertyDetailsCard from './PropertyDetailsCard';
import { MdLocationPin } from 'react-icons/md';
import { SlCalender } from 'react-icons/sl';
import TransactionLogs from './TransactionLogs';
import ControlledCarousel from 'components/Carousel/ControlledCarousel';
import { viewPercentage, viewAmount, getYearsView } from 'utils/number';
import { getStateName } from 'utils/address';
import { viewDateAndTime } from 'utils/date';

const TransactionDetails = () => {
    const { id } = useParams();

    const { data, isLoading, isError } = useGetTransactionByIdQuery(id);

    return (isLoading)
        ? (<LoadingDataCard />)
        : (isError)
            ? (<ErrorCard />)
            : data // data
                ? (
                    <React.Fragment>
                        <div>
                            <Flex my="20px" gap="10px" justify="space-between">
                                <Box p="8px" h="80px" w="180px" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                    <p style={{ fontWeight: 'bold', fontSize: 'smaller' }}>Credit Score</p>
                                    <p style={{ marginTop: '5px' }}>{data.metaData.creditScore}</p>
                                </Box>
                                <Box p="8px" h="80px" w="180px" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                    <p style={{ fontWeight: 'bold', fontSize: 'smaller' }}>Loan to Value (LTV)</p>
                                    <p style={{ marginTop: '5px' }}>{viewPercentage(data.metaData.LTV)}</p>
                                </Box>
                                <Box p="8px" h="80px" w="180px" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                    <p style={{ fontWeight: 'bold', fontSize: 'smaller' }}>Combined Loan to Value (CLTV)</p>
                                    <p style={{ marginTop: '5px' }}>{viewPercentage(data.metaData.CLTV)}</p>
                                </Box>
                                <Box p="8px" h="80px" w="180px" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                    <p style={{ fontWeight: 'bold', fontSize: 'smaller' }}>APR (Annual Percentage Rate)</p>
                                    <p style={{ marginTop: '5px' }}>{viewPercentage(data.metaData.APR)}</p>
                                </Box>
                                <Box p="8px" h="80px" w="180px" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                    <p style={{ fontWeight: 'bold', fontSize: 'smaller' }}>Total Monthly Payment (Principal & Interest)</p>
                                    <p style={{ marginTop: '5px' }}>{viewAmount(data.monthly.PrincipleAndInterest)}</p>
                                </Box>
                            </Flex>
                        </div>
                        <Grid templateColumns='repeat(2, 1fr)' gap={4}>
                            <Box h="600px" mt="20px" p="8px" w="100%" borderWidth="1px" borderColor="gray.500" borderRadius="5px">
                                <Box mb="5px">
                                    <ControlledCarousel images={data.property.images}/>
                                </Box>
                                <Flex alignItems="center" gap={4}>
                                    <Text color="brand.500">{data.applicationId}</Text>
                                    <Text fontWeight='bold' py="0.5px" px="14px" color="brand.500" bgColor="gray.200">{data.applicationStatus}</Text>
                                </Flex>
                                <Text my="5px" color="brand.500">{data.applicationName}</Text>
                                <Text fontSize="lg" fontWeight="bold">{data.loanPurpose}</Text>
                                <Flex my="5px" alignItems="center" gap={2}>
                                    <MdLocationPin color="#167C85" size="20px"/>
                                    <Text>{getStateName(data.property.Address.State)}</Text>
                                </Flex>
                                <Flex my="5px" justifyContent="space-between">
                                    <Box>
                                        <Flex alignItems="center" gap={5}>
                                            <Text>{viewPercentage(data.proposePercentage)}</Text>
                                            <Flex alignItems="center" gap={1}>
                                                <SlCalender color="#167C85"/>
                                                <Text >{getYearsView(data.noOfYears)}</Text>
                                            </Flex>
                                        </Flex>
                                    </Box>
                                    <Text fontSize="lg" fontWeight="bold">{viewAmount(data.loanAmount)}</Text>
                                </Flex>
                                <Text mt="8px" >{viewDateAndTime(data.createdAt)}</Text>

                                <HStack my="15px" divider={<StackDivider borderColor='gray.400' />}>
                                    <Box w="130px">
                                        <Text my="5px">Property Type</Text>
                                        <Text fontWeight="bold" isTruncated>{data.property.propertyType}</Text>
                                    </Box>
                                    <Box w="130px">
                                        <Text my="5px">Occupancy Type</Text>
                                        <Text fontWeight="bold" isTruncated>{data.property.OccupencyType}</Text>
                                    </Box>
                                    <Box w="130px">
                                        <Text my="5px">Zipcode</Text>
                                        <Text fontWeight="bold">{data.property.Address.Zipcode}</Text>
                                    </Box>
                                </HStack>

                                <HStack my="20px" divider={<StackDivider borderColor='gray.400' />}>
                                    <Box w="130px" p="5px" >
                                        <Text my="5px">Estimated Value</Text>
                                        <Text fontWeight="bold">{viewAmount(data.loanAmount)}</Text>
                                    </Box>
                                    <Box w="130px">
                                        <Text my="5px">Credit Score</Text>
                                        <Text fontWeight="bold">{data.metaData.creditScore}</Text>
                                    </Box>
                                    <Box w="130px">
                                        <Text my="5px">Debt Ratio</Text>
                                        <Text fontWeight="bold">{viewPercentage(data.property.debtRatio)}</Text>
                                    </Box>
                                </HStack>
                            </Box>
                            <Tabs isFitted variant="solid-rounded" mt="20px">
                                <TabList w={'100%'}>
                                    <Tab w={'50%'}>Property Details</Tab>
                                    <Tab w={'50%'}>Transaction Details</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel>
                                        <PropertyDetailsCard data={data} />
                                    </TabPanel>
                                    <TabPanel>
                                        <TransactionDetailsCard data={data} />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Grid>
                        <Box mt="20px">
                            <TransactionLogs data={ data.logs }/>
                        </Box>
                    </React.Fragment>
                )
                : <NoDataCard label={'No Details Available'}/>;
};

export default TransactionDetails;
